import { type PaletteKey, PixelCanvas } from '../../lib';
import type { Part, RibbonKey } from '../body';
import type { Worn } from '../headwear';
import { DEAD_FACE, FACE } from '../looks';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';

/**
 * The Reaper set (ADR-0123, Season 1 "Harvest"): a deep hood, a tattered robe and the Bone finish on the guns. A season's
 * set: only the pass gives it, never the Store.
 *
 * The hood is headwear tied on (`Worn`, ADR-0089): it frames the face, so it must move with the head (a hat lags it a
 * frame and would slide off the face), and a reaper stays hooded knocked out. Its silhouette is its own at game zoom: a
 * dark bell from a short peak leaning back over the crown down to a mantle on the shoulders (the pumpkin is a round ball,
 * the gnome a tall cone in the team's colour), the face deep in a pointed-arch opening. Inside, the hood's shade covers the
 * forehead down to the brows and stands off the cheeks, so the face sits in the dark and its eyes still read. The team
 * is the lining where the opening's rim turns back (a ring round the face), the robe's rope belt and its cords, and the
 * trousers under its torn hem. Every part is procedural over a few shapes (the dome, the peak's spine, the arch), lit
 * from the top-left in the cloth's ramp (black slate with a purple sheen), so the standing and lying hood agree; on the
 * run the robe's back streams out behind (the ribbon's place).
 *
 * Rubric (review sheets at 4×-9× and 1:1 on grass and lane, every team, idle, run and dead; the wardrobe's tiles; the
 * `lod-sets`, `sets`, `lod-menus` and `looks` playtests at 1280×720 to 2560×1440): hood S5 R4 P4 L4 C4 A4 F4, lying S4
 * R4 P4 L4 C4 F4, robe S4 R4 P4 L4 C4 F4, Bone (v2, beside Stock, Ivory and Cosmos) R5 P5 L4 F4. Still weak: on the slate cobble the dark cloth leans on its
 * outline; the robe shows little between the mantle and the gun (its belt, cords and torn hem carry it); the light
 * steps on the dome at 8× and the lit purple's sel-out is brown; lying, the mantle hides the robe's belt (the lining
 * and the trousers carry the team there); Bone's brass shadow is a pinkish band under the Pollinator's body and some
 * guns' dark steel turns to a brown block (a shotgun's receiver).
 */

// --- The cloth -------------------------------------------------------------------------------------------------------

/** The robe's cloth (the hood's too), dark to lit: black slate with a purple sheen where the light falls. */
const CLOTH = { deep: 'k', shade: '1', mid: '2', lit: 'P' } as const satisfies Record<string, PaletteKey>;
type Tone = keyof typeof CLOTH;
const DARKER: Record<Tone, Tone> = { lit: 'mid', mid: 'shade', shade: 'deep', deep: 'deep' };
const LIGHTER: Record<Tone, Tone> = { lit: 'lit', mid: 'lit', shade: 'mid', deep: 'shade' };
/** Light on a surface whose normal is (u, v, z) in picture space (x right, y down, z toward us): from the top-left. */
const light = (u: number, v: number, z: number) => -0.62 * u - 0.55 * v + 0.55 * z;
const tone = (l: number): Tone => (l > 0.8 ? 'lit' : l > 0.32 ? 'mid' : l > -0.2 ? 'shade' : 'deep');

type Ellipse = { cx: number; cy: number; rx: number; ry: number };
const within = (x: number, y: number, e: Ellipse, grow = 0) =>
  ((x - e.cx) / (e.rx + grow)) ** 2 + ((y - e.cy) / (e.ry + grow)) ** 2 <= 1;

type Curve = readonly [readonly [number, number], readonly [number, number], readonly [number, number]];
/** A quadratic curve through three points, at t. */
const bezier = ([a, b, c]: Curve, t: number): [number, number] => {
  const s = 1 - t;
  return [s * s * a[0] + 2 * s * t * b[0] + t * t * c[0], s * s * a[1] + 2 * s * t * b[1] + t * t * c[1]];
};
/** How far (x, y) is from `curve`, sampled. */
const fromCurve = (curve: Curve, x: number, y: number) => {
  let d = Infinity;
  for (let t = 0; t <= 1; t += 0.02) {
    const [cx, cy] = bezier(curve, t);
    d = Math.min(d, Math.hypot(x - cx, y - cy));
  }
  return d;
};

/** A mask of `inside` over a `w` × `h` canvas at (x0, y0) of the frame, tested at pixel centres. */
function mask(w: number, h: number, x0: number, y0: number, inside: (x: number, y: number) => boolean) {
  const m = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) m[y * w + x] = inside(x + x0 + 0.5, y + y0 + 0.5) ? 1 : 0;
  return (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && m[y * w + x] === 1;
}

/**
 * Cloth in tone `t` at (x, y) of a part whose silhouette is `inside`: never the deepest tone on the silhouette's edge
 * (the sel-out picks a rim by the edge's colour, and black's is brown).
 */
const clothAt = (inside: (x: number, y: number) => boolean, x: number, y: number, t: Tone): PaletteKey =>
  t === 'deep' && !(inside(x - 1, y) && inside(x + 1, y) && inside(x, y - 1) && inside(x, y + 1))
    ? CLOTH.shade
    : CLOTH[t];

// --- The hood --------------------------------------------------------------------------------------------------------

/** Over the head (frame px): its dome, the face's opening (a pointed arch, open under the chin), the face seen in it. */
const DOME: Ellipse = { cx: 31, cy: 28, rx: 24, ry: 15.5 };
const OPEN: Ellipse = { cx: 36.5, cy: 36, rx: 14.5, ry: 13.5 };
const CHIN = 44;
const SEEN: Ellipse = { cx: 36.5, cy: 38.5, rx: 11, ry: 10 };
/** The rim round the opening: the team's lining (2 px) where it turns back, then its crest (the cloth's edge). */
const LINING = 2.2;
const CREST = 3.4;
/** The peak's spine, from inside the dome up and a little back to the tip, and its half-width at the root. */
const SPINE: Curve = [
  [34, 20],
  [29, 9],
  [23, 6.5],
];
const PEAK_W = 10;
/** The seam where the hood's halves meet: from the opening's point over the crown into the peak. */
const SEAM: Curve = [
  [36.5, 20],
  [33, 12],
  [26, 8],
];
/** The mantle's ragged hem over the shoulders: each 6-px tooth's depth, ragged at the back, calmer at the front. */
const TEETH = [3, 2, 3, 1, 1, 1, 1, 1, 2];
const hem = (x: number) => {
  const i = Math.floor((x - 2) / 6);
  const f = ((x - 2) % 6) / 6;
  return 45.5 + (TEETH[i % TEETH.length] ?? 2) * (1 - Math.abs(f * 2 - 1));
};
/** Folds down the mantle: a crease from (x, y) to (x, y), lit on its left. */
const FOLDS: readonly (readonly [number, number, number, number])[] = [
  [11, 31, 8, 47],
  [17, 34, 15, 48],
];

/** Where on the peak (x, y) is: how far along its spine (0 root, 1 tip) and how far across (-1 left … 1 right); or null. */
function onPeak(x: number, y: number): { t: number; across: number } | null {
  for (let t = 0; t <= 1; t += 0.01) {
    const [px, py] = bezier(SPINE, t);
    const w = PEAK_W * (1 - t) ** 1.1;
    const d = Math.hypot(x - px, y - py);
    if (d <= w) {
      const [qx, qy] = bezier(SPINE, Math.min(1, t + 0.01));
      const side = Math.sign((qx - px) * (y - py) - (qy - py) * (x - px));
      return { t, across: (side * d) / Math.max(w, 0.5) };
    }
  }
  return null;
}

const inDome = (x: number, y: number) => {
  if (y <= DOME.cy) return within(x, y, DOME);
  const left = DOME.cx - DOME.rx - (y - DOME.cy) * 0.14;
  const right = DOME.cx + DOME.rx + (y - DOME.cy) * 0.02;
  return x >= left && x <= right && y <= hem(x);
};

/** Whether (x, y) is in the opening grown by `grow` px: an arch pointed at the top, round below, cut at the chin. */
function inOpening(x: number, y: number, grow = 0): boolean {
  if (y >= CHIN + grow * 0.5) return false;
  const X = Math.abs(x - OPEN.cx) / (OPEN.rx + grow);
  const Y = (OPEN.cy - y) / (OPEN.ry + grow);
  if (Y <= 0) return X * X + Y * Y <= 1;
  return Y < 1 && X <= (1 - Y ** 1.5) ** 0.8;
}

/** The face shows below this line in the opening; above it, the hood's shade. */
const shadeLine = (x: number) => 30.5 + ((x - SEEN.cx) / SEEN.rx) ** 2 * 4;

/**
 * The opening's rim at (x, y) (in the opening grown by `CREST`, not in it): the team's lining, darker where it turns into
 * the hood, lit up its left; then the cloth's crest.
 */
function rim(x: number, y: number): PaletteKey {
  const a = Math.atan2(y - OPEN.cy, x - OPEN.cx);
  const l = -Math.cos(a) * 0.7 - Math.sin(a) * 0.6;
  if (inOpening(x, y, LINING * 0.5)) return l > 0.2 ? 'b' : 'B';
  if (inOpening(x, y, LINING)) return l > 0.2 ? 'c' : l > -0.5 ? 'b' : 'B';
  return CLOTH[l > 0.1 ? 'lit' : 'mid'];
}

/**
 * The hood (64 × 52 from the frame's top-left), over the face in the hair's place. `alone`: on nobody (the wardrobe's
 * picture), its opening all shade.
 */
function hood(alone = false): Part {
  const c = new PixelCanvas(64, 52);
  const inside = mask(c.w, c.h, 0, 0, (x, y) => inDome(x, y) || onPeak(x, y) !== null);
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      if (!inside(x, y)) continue;
      const [px, py] = [x + 0.5, y + 0.5];
      // The opening: the face in it, or the hood's dark inside round it.
      if (inOpening(px, py)) {
        const face = FACE[0].get(x - FACE[1], y - FACE[2]) !== null;
        if (!alone && face && within(px, py, SEEN) && py > shadeLine(px)) continue;
        // On nobody, its inside's back wall shows, dark under the overhang.
        const wall = alone && py > 31;
        c.set(x, y, (py > 38 && !inOpening(px, py, -1.5)) || wall ? CLOTH.shade : CLOTH.deep);
        continue;
      }
      if (inOpening(px, py, CREST)) {
        c.set(x, y, rim(px, py));
        continue;
      }
      const peak = within(px, py, DOME, -1) ? null : onPeak(px, py);
      let t: Tone;
      if (peak) {
        const l = -0.8 * peak.across + 0.3 - peak.t * 0.2;
        t = l > 0.5 ? 'lit' : l > 0 ? 'mid' : 'shade';
      } else {
        const below = py > DOME.cy;
        const u = (px - DOME.cx) / (DOME.rx + (below ? (py - DOME.cy) * 0.08 : 0));
        const v = below ? Math.min(0.6, Math.max(0, (py - 38) / 14)) : (py - DOME.cy) / DOME.ry;
        t = tone(light(u, v, Math.sqrt(Math.max(0, 1 - u * u - v * v))));
      }
      // The seam over the crown (a crease, its left side lit) and the folds down the mantle.
      const seam = fromCurve(SEAM, px, py);
      if (seam < 0.6) t = DARKER[t];
      else if (seam < 1.6 && px < bezier(SEAM, 0.5)[0] + (py - bezier(SEAM, 0.5)[1]) * -0.4) t = LIGHTER[t];
      for (const [x0, y0, x1, y1] of FOLDS) {
        if (py < y0 || py > y1) continue;
        const fx = x0 + ((x1 - x0) * (py - y0)) / (y1 - y0);
        if (Math.abs(px - fx) < 0.5) t = DARKER[t];
        else if (Math.abs(px + 1 - fx) < 0.5) t = LIGHTER[t];
      }
      // The hem's last row, turned under: a step darker.
      if (!inside(x, y + 1) && py > 40) t = DARKER[t];
      c.set(x, y, clothAt(inside, x, y, t));
    }
  }
  return [c, 0, 0];
}

/**
 * The hood lying on the grass round the dead pose's head (seen from above: the chin left, the crown right), as it is
 * standing turned a quarter: the opening's point past the brow, the face in it, its shade over the forehead, the lining
 * round it; the cloth spread round the head, the peak flopped over at the top right, the mantle under the chin over the
 * robe's shoulders.
 */
function deadHood(): Part {
  const c = new PixelCanvas(64, 72);
  const [fx, fy] = [DEAD_FACE[1], DEAD_FACE[2]];
  // The standing hood's picture space for a point here (the head turned a quarter: its top to the right).
  const stand = (x: number, y: number): [number, number] => [OPEN.cx + (y - 56.5), OPEN.cy - (x - 35.7) / 1.087];
  const spread: Ellipse = { cx: 44, cy: 56.5, rx: 15.5, ry: 14 };
  const peak: Curve = [
    [52, 46],
    [56, 41],
    [60, 41],
  ];
  const ragged = (y: number) => 22 + [2, 0, 1, 3, 1, 0, 2][Math.floor(y / 2) % 7]!;
  const inside = mask(c.w, c.h, 0, 0, (x, y) => {
    if (y > 70) return false;
    if (within(x, y, spread)) return true;
    if (x >= ragged(y) && x < 36 && y > 43 && y < 70) return true;
    for (let t = 0; t <= 1; t += 0.02) {
      const [px, py] = bezier(peak, t);
      if (Math.hypot(x - px, y - py) <= 5 * (1 - t)) return true;
    }
    return false;
  });
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      if (!inside(x, y)) continue;
      const [px, py] = [x + 0.5, y + 0.5];
      const [sx, sy] = stand(px, py);
      // Round the brow its dark inside and the lining; round the jaw the hood's sides hug the face (its cloth).
      const face = DEAD_FACE[0].get(x - fx, y - fy) !== null;
      if (inOpening(sx, sy) && (face || px > 37)) {
        if (face && sy > shadeLine(sx)) continue;
        c.set(x, y, CLOTH.deep);
        continue;
      }
      if (inOpening(sx, sy, CREST) && px > 35) {
        c.set(x, y, rim(sx, sy));
        continue;
      }
      const u = (px - spread.cx) / (spread.rx + 4);
      const v = (py - spread.cy) / (spread.ry + 4);
      let t = tone(light(u, v, Math.sqrt(Math.max(0, 1 - u * u - v * v))) + 0.1);
      // Folds radiating from the head over the mantle.
      if (px < 34 && (Math.round(py) % 5 === 0 || Math.round(py) % 5 === 2)) t = DARKER[t];
      c.set(x, y, clothAt(inside, x, y, t));
    }
  }
  return [c, 0, 0];
}

/** A strip of the robe's back streaming behind on the run (ribbon space: y from `HAT_Y`), ragged along its edge. */
function cape(wave: number, phase: number): Part {
  const c = new PixelCanvas(20, 14);
  const [x0, y0] = [-4, 26];
  const top = (x: number) => 31 + Math.sin((x + phase) * 0.6) * wave + (12 - x) * 0.18;
  const width = (x: number) => 2.5 + (x + 2) * 0.55;
  const inside = mask(c.w, c.h, x0, y0, (x, y) => {
    if (x < -2 || x > 13) return false;
    const notch = (Math.floor(x + phase) % 4 === 0 ? 2 : 0) + (x < 0 ? 1 : 0);
    return y >= top(x) && y <= top(x) + width(x) - notch;
  });
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      if (!inside(x, y)) continue;
      c.set(x, y, clothAt(inside, x, y, !inside(x, y - 1) ? 'lit' : !inside(x, y + 1) ? 'shade' : 'mid'));
    }
  }
  return [c, x0, y0];
}

/** At rest the robe's back hangs behind the mantle, out of sight; on the run it streams out behind. */
const NONE: Part = [new PixelCanvas(1, 1), 0, 0];
const TAILS: Record<RibbonKey, Part> = {
  hang: NONE,
  hang2: NONE,
  flowA: cape(1.2, 0),
  flowB: cape(1.6, 2),
  flowC: cape(1.2, 4),
};

const HOOD = (): Worn => ({ worn: true, front: hood(), alone: hood(true), ribbon: TAILS, dead: deadHood() });

// --- The robe --------------------------------------------------------------------------------------------------------

/** The robe's top-left in the frame. */
const ROBE_AT = [6, 40] as const;
/** Its hem, ragged: each 5-px tooth's depth, longer at the back. */
const ROBE_TEETH = [4, 3, 4, 2, 3, 2, 3, 2, 3];
const robeHem = (x: number) => {
  const i = Math.floor((x - 6) / 6);
  const f = ((x - 6) % 6) / 6;
  return 57 + (30 - x) * 0.1 + (ROBE_TEETH[i % ROBE_TEETH.length] ?? 2) * (1 - Math.abs(f * 2 - 1));
};
/** Its sides: sloping shoulders, then flaring to the hem. */
const shoulder = (y: number) => Math.max(0, 45 - y) ** 1.5 * 0.9;
const robeLeft = (y: number) => 13.5 - (y - 41) * 0.32 + shoulder(y);
const robeRight = (y: number) => 47 + (y - 41) * 0.18 - shoulder(y);
/** The neckline: a V open on the look's skin, edged in the team's lining like the hood. */
const NECK = { x: 31.5, y: 46 } as const;
const neck = (x: number, y: number) => Math.abs(x - NECK.x) / 1.1 < NECK.y - y;
/** The rope belt's top row (it dips a px round the front), the knot's middle on its left (out of the gun's way). */
const belt = (x: number) => 51 + (x > 22 && x < 42 ? 1 : 0);
const KNOT = [17, 52] as const;

/**
 * The robe standing (the frame's x 6-57, y 40-65): black slate lit purple down its left, folds flaring from the belt to a
 * hem torn in points (longer at the back), so the team's trousers show from the knee; a rope belt in the team's
 * colours, knotted on the left, its two cords hanging to tassels.
 */
function robe(): Part {
  const [x0, y0] = ROBE_AT;
  const c = new PixelCanvas(52, 26);
  const inside = mask(
    c.w,
    c.h,
    x0,
    y0,
    (px, py) => py >= 41 && px >= robeLeft(py) && px <= robeRight(py) && py <= robeHem(px),
  );
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      if (!inside(x, y)) continue;
      const [px, py] = [x + x0 + 0.5, y + y0 + 0.5];
      const half = (robeRight(py) - robeLeft(py)) / 2;
      const u = (px - robeLeft(py) - half) / half;
      let t = tone(light(u * 0.95, py < 46 ? -0.4 : 0.1, Math.sqrt(Math.max(0, 1 - u * u))));
      // Folds flaring down from the belt: a crease, lit on its left.
      if (py > 55)
        for (const f of [15, 24, 33, 41]) {
          const fx = f + (f - 30) * 0.06 * (py - 55);
          if (Math.abs(px - fx) < 0.5) t = DARKER[t];
          else if (Math.abs(px + 1 - fx) < 0.5) t = LIGHTER[t];
        }
      c.set(x, y, clothAt(inside, x, y, t));
      // The neckline (under the hood's mantle in a match; the wardrobe shows it).
      if (neck(px, py)) c.set(x, y, neck(px, py + 1.5) ? 's' : 'S');
      else if (py < 45 && (neck(px - 1, py) || neck(px + 1, py))) c.set(x, y, px < NECK.x ? 'b' : 'B');
    }
  }
  // The rope belt, twisted (light strands slanting), in the team's colours, shaded on the right.
  for (let x = x0; x < x0 + c.w; x++) {
    const b = belt(x);
    for (let y = b; y < b + 3; y++) {
      if (!inside(x - x0, y - y0)) continue;
      const right = x > 40;
      const strand = (x + y) % 3 === 0;
      at(x, y, y === b + 2 ? 'B' : strand ? (right ? 'b' : 'c') : right ? 'B' : 'b');
    }
  }
  // The knot and its two cords, hanging to tassels.
  c.stamp(
    `
    .cbb.
    cbbbB
    cbcbB
    bbbBB
    .BBB.
  `,
    KNOT[0] - 2 - x0,
    KNOT[1] - 2 - y0,
  );
  for (const [dx, len] of [
    [-1, 6],
    [2, 4],
  ] as const) {
    const x = KNOT[0] + dx;
    for (let i = 0; i < len; i++) at(x + (i > len / 2 ? Math.sign(dx) : 0), KNOT[1] + 3 + i, i % 2 ? 'b' : 'c');
    const [tx, ty] = [x + Math.sign(dx), KNOT[1] + 3 + len];
    at(tx - 1, ty, 'b');
    at(tx, ty, 'b');
    at(tx + 1, ty, 'B');
    at(tx, ty + 1, 'B');
  }
  return [c, x0, y0];
}

/**
 * The robe lying (the frame's x 20-33, y 49-70): its torn skirt over the hips (left; the team's trousers still show past
 * it), the rope belt across the waist with its knot, the body under the head.
 */
function deadRobe(): Part {
  const [x0, y0] = [19, 49];
  const c = new PixelCanvas(15, 22);
  const tear = (y: number) => 20 + [0, 2, 1, 3, 0, 2, 3, 1][Math.floor(y / 2) % 8]!;
  const inside = mask(c.w, c.h, x0, y0, (x, y) => y >= 50 && y <= 69.5 && x >= tear(y) && x <= 33.5);
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      if (!inside(x, y)) continue;
      const [px, py] = [x + x0 + 0.5, y + y0 + 0.5];
      if (px > 24 && px < 27) {
        // The belt across the waist (a column lying), its knot near the top.
        c.set(x, y, py < 55 && py > 51 ? (px < 26 ? 'c' : 'b') : Math.round(py) % 3 === 0 ? 'c' : 'b');
        continue;
      }
      const t = tone(light(0, (py - 59) / 12, 0.8) + (px < 25 ? 0.15 : 0));
      c.set(x, y, clothAt(inside, x, y, t));
    }
  }
  return [c, x0, y0];
}

const ROBE_OUTFIT = (): Outfit => ({ torso: robe(), dead: deadRobe() });

// --- Bone ------------------------------------------------------------------------------------------------------------

/**
 * The Bone finish: the whole gun bone-white, the steel's body cream (`e`) with white highlights (`w`) and dark brown seams
 * (`n`, its darkest steps `N`: the outline's steps stay dark), the brass bone too (`e`, its shadow the warm `S`), the wood
 * a dark weathered plum (`k N`, a purple sheen `P`) and the brightest glint a ghostly cyan (`c`). A step whiter than
 * Ivory (a tan `t e w` steel, brown wood, its brass left gold) and warm where Cosmos is a cool grey hull with navy wood.
 */
const BONE = {
  k: 'N',
  '1': 'N',
  '2': 'n',
  '3': 'e',
  '4': 'w',
  '5': 'w',
  N: 'k',
  n: 'k',
  m: 'N',
  t: 'P',
  e: 'P',
  U: 'S',
  Y: 'e',
  y: 'c',
} as const satisfies Partial<Record<PaletteKey, PaletteKey>>;

export const REAPER = drawnLater({ head: HOOD, outfit: ROBE_OUTFIT, gun: BONE });
